import { BRAND } from "@/lib/constants";
import { Instagram, Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { Diya } from "@/components/landing/Decorations";
import StationeryWalaPopup from "@/components/landing/StationeryWalaPopup";
import { useState } from "react";
import { Link } from "react-router-dom";

export default function Footer() {
  const [popupOpen, setPopupOpen] = useState(false);
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative pt-24 md:pt-32 pb-10 border-t border-white/10 overflow-hidden"
      style={{ background: "var(--iw-bg-3)" }}
    >
      {/* warm glow from top */}
      <div
        aria-hidden="true"
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[500px] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(232,165,180,0.22) 0%, rgba(212,175,55,0.12) 35%, rgba(0,0,0,0) 70%)",
          filter: "blur(20px)",
        }}
      />

      {/* Corner diyas */}
      <div className="absolute top-16 right-10 z-10 pointer-events-none hidden md:block">
        <Diya size={32} />
      </div>
      <div className="absolute top-16 left-10 z-10 pointer-events-none hidden md:block">
        <Diya size={32} delay={0.5} />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Huge marquee line */}
        <div className="mb-16 md:mb-20">
          <h2
            className="font-serif-display text-[var(--iw-ivory)] text-[16vw] md:text-[11vw] leading-[0.9] tracking-[-0.03em]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Let's design your
            <br />
            <span className="italic gold-rose-gradient">
              invite
            </span>
            <span className="text-[var(--iw-rose)]">.</span>
          </h2>
          <div className="mt-10">
            <button
              onClick={() => scrollTo("schedule")}
              className="btn-gold"
              data-testid="footer-cta"
            >
              Start on WhatsApp
            </button>
          </div>
        </div>

        <div className="hairline my-12" />

        <div className="grid md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src={BRAND.logo}
                alt={`${BRAND.name} — premium digital wedding invitation studio logo`}
                className="h-12 md:h-14 w-auto object-contain"
                width="120"
                height="56"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="text-[var(--iw-muted)] text-sm leading-relaxed max-w-xs">
              We create premium digital invitations designed to make a lasting
              impression — films, reels, stationery and more.
            </p>

            {/* Sister brand link */}
            <button
              onClick={() => setPopupOpen(true)}
              className="mt-6 flex items-center gap-3 p-3 border border-white/10 hover:border-[var(--iw-gold)]/40 transition-colors group max-w-xs text-left w-full"
              data-testid="sister-brand-link"
            >
              <img
                src={BRAND.sister.logo}
                alt={`${BRAND.sister.name} — Stationery Wala printed wedding stationery sister brand logo`}
                className="h-10 w-auto object-contain"
                width="80"
                height="40"
                loading="lazy"
                decoding="async"
              />
              <div className="flex-1 min-w-0">
                <div className="overline text-[var(--iw-faint)] mb-1">
                  Our Sister Brand · Tap to explore
                </div>
                <div className="text-[var(--iw-ivory-dim)] text-xs leading-snug group-hover:text-[var(--iw-gold)] transition-colors">
                  {BRAND.sister.tagline}
                </div>
              </div>
            </button>
          </div>

          <div>
            <div className="overline text-[var(--iw-faint)] mb-4">Studio</div>
            <ul className="space-y-3 text-[var(--iw-ivory-dim)] text-sm">
              {["portfolio", "services", "stories"].map((l) => (
                <li key={l}>
                  <button
                    onClick={() => scrollTo(l)}
                    className="link-underline capitalize"
                    data-testid={`footer-scroll-${l}`}
                  >
                    {l}
                  </button>
                </li>
              ))}
              <li>
                <Link
                  to="/careers"
                  className="link-underline inline-flex items-center gap-1.5"
                  data-testid="footer-link-careers"
                >
                  Careers
                  <span
                    className="overline text-[9px] px-1.5 py-0.5"
                    style={{
                      background: "rgba(232,165,180,0.15)",
                      color: "var(--iw-rose)",
                      border: "1px solid rgba(232,165,180,0.35)",
                    }}
                  >
                    We're hiring
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="link-underline"
                  data-testid="footer-link-services"
                >
                  All Services
                </Link>
              </li>
              <li>
                <Link
                  to="/work"
                  className="link-underline"
                  data-testid="footer-link-work"
                >
                  Case Studies
                </Link>
              </li>
              <li>
                <Link
                  to="/locations"
                  className="link-underline"
                  data-testid="footer-link-locations"
                >
                  Cities we serve
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="link-underline"
                  data-testid="footer-link-blog"
                >
                  The Journal
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <div className="overline text-[var(--iw-faint)] mb-4">Reach Us</div>
            <ul className="space-y-3 text-[var(--iw-ivory-dim)] text-sm">
              <li className="flex items-center gap-3">
                <Mail size={14} className="text-[var(--iw-gold)]" />
                <a
                  href={`mailto:${BRAND.email}`}
                  className="link-underline"
                  data-testid="footer-email"
                >
                  {BRAND.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={14} className="text-[var(--iw-gold)]" />
                <a
                  href={`tel:${BRAND.whatsapp}`}
                  className="link-underline"
                  data-testid="footer-phone"
                >
                  {BRAND.whatsappDisplay}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle size={14} className="text-[var(--iw-gold)]" />
                <a
                  href={`https://wa.me/${BRAND.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                  className="link-underline"
                  data-testid="footer-whatsapp"
                >
                  WhatsApp
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram size={14} className="text-[var(--iw-gold)]" />
                <a
                  href={BRAND.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="link-underline"
                  data-testid="footer-instagram"
                >
                  @{BRAND.instagram.toLowerCase()}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="overline text-[var(--iw-faint)] mb-4">Atelier</div>
            <div className="flex items-start gap-3 text-[var(--iw-ivory-dim)] text-sm">
              <MapPin size={14} className="text-[var(--iw-gold)] mt-1 shrink-0" />
              <a
                href="https://maps.google.com/?q=Spectrum+Metro+Mall+Noida"
                target="_blank"
                rel="noreferrer"
                className="link-underline leading-relaxed"
                data-testid="footer-address"
              >
                {BRAND.address}
              </a>
            </div>
            <p className="text-[var(--iw-ivory-dim)] text-sm leading-relaxed mt-4">
              DM us anytime on Instagram or WhatsApp.
              <br />
              Replies within a few hours · Mon – Sun
            </p>
          </div>
        </div>

        <div className="hairline my-10" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="overline text-[var(--iw-faint)]">
            © {new Date().getFullYear()} {BRAND.name} — All rights reserved.
          </div>
          <div className="overline text-[var(--iw-faint)]">
            Crafted with ❤ in India · For couples everywhere
          </div>
        </div>
      </div>
      <StationeryWalaPopup open={popupOpen} onClose={() => setPopupOpen(false)} />
    </footer>
  );
}